import {BillingProps} from 'modules/billings/types';
import billingsKeys from 'modules/billings/util/billings-keys';
import React from 'react';
import StatusButton from 'shared/components/status-button';
import List from 'shared/layout/list';
import useProjection from 'shared/migration-hooks/use-projection';
import dateFnsService from 'shared/services/date-fns-service';
import toBrl from 'shared/util/to-brl';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import {MdCurrencyExchange} from 'react-icons/md';
import {Operators} from 'shared/migration-components/filter-list/types';

const Billings: React.FC = () => {
  const projection = useProjection<BillingProps>(
    'billings',
    [
      'id',
      'company.name',
      'month',
      'startDate',
      'endDate',
      'rawTotal',
      'companyAmount',
      'paid',
      'isClosed',
      'expiresAt',
    ],
    {
      defaultFilters: {
        isClosed: {operator: Operators.Equals, value: true},
        paid: {operator: Operators.Equals, value: false},
      },
      overrideDefaultValues: true,
    },
  );

  const {companyId} = useAccessControls();

  return (
    <List<BillingProps>
      {...{
        titleBar: {
          title: 'Faturas',
        },
        analyticsCards: !companyId
          ? [
              {
                field: 'companyAmount',
                label: 'Repasse empresa',
                icon: <MdCurrencyExchange />,
                customValue: toBrl,
              },
            ]
          : undefined,
        table: {
          url: '/billing',
          projection: {
            ...projection,
            options: [
              {value: 'company.name', hidden: !!companyId},
              'month',
              {
                value: 'startDate',
                column: (row) => dateFnsService.dateTimeFormat(row.startDate),
                filter: {type: 'date'},
              },
              {
                value: 'endDate',
                column: (row) => dateFnsService.dateTimeFormat(row.endDate),
                filter: {type: 'date'},
              },
              {value: 'rawTotal', column: (row) => toBrl(row.rawTotal)},
              {
                label: companyId ? 'Total a receber' : undefined,
                value: 'companyAmount',
                column: (row) => toBrl(row.companyAmount),
              },
              {
                value: 'franchiseAmount',
                column: (row) => toBrl(row.franchiseAmount),
                hidden: !!companyId,
              },
              {
                value: 'cityAmount',
                column: (row) => toBrl(row.cityAmount),
                hidden: !!companyId,
              },
              {
                value: 'paid',
                column: (row) => (
                  <StatusButton
                    text={row.paid ? 'Sim' : 'Não'}
                    variant={row.paid ? 'success' : 'error'}
                  />
                ),
                filter: {
                  type: 'select',
                  values: [
                    {label: 'Sim', value: true},
                    {label: 'Não', value: false},
                  ],
                },
              },
              {
                value: 'isClosed',
                column: (row) => (
                  <StatusButton
                    text={row.isClosed ? 'Sim' : 'Não'}
                    variant={row.isClosed ? 'success' : 'error'}
                  />
                ),
                filter: {
                  type: 'select',
                  values: [
                    {label: 'Sim', value: true},
                    {label: 'Não', value: false},
                  ],
                },
              },
              {
                value: 'expiresAt',
                column: (row) => dateFnsService.dateFormat(row.expiresAt),
              },
              'company.pixKey',
            ],
          },
          translatedPropsKey: 'BillingProps',
        },
        queryKey: billingsKeys.list(),
      }}
    />
  );
};

export default Billings;
